import { XCircleIcon } from "@heroicons/react/20/solid";
import { PropsWithChildren, useEffect, useState } from "react";

interface AlertErrorProps extends PropsWithChildren {
	position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
	errorTitle: string;
	errors: Array<string>;
	duration: number;
	onClose: any;
}

interface Pos {
	top: string | null;
	right: string | null;
	bottom: string | null;
	left: string | null;
}

function calculatePos(
	position: string | undefined,
	distance: string = "50px",
): Pos {
	switch (position) {
		case "bottom-left":
			return {
				top: null,
				right: null,
				bottom: distance,
				left: distance,
			};
		case "bottom-right":
			return {
				top: null,
				right: distance,
				bottom: distance,
				left: null,
			};
		case "top-left":
			return {
				top: distance,
				right: null,
				bottom: null,
				left: distance,
			};
		case "top-right":
			return {
				top: distance,
				right: distance,
				bottom: null,
				left: null,
			};

		default:
			return {
				top: null,
				right: null,
				bottom: distance,
				left: distance,
			};
	}
}

export default function AlertError(Props: AlertErrorProps) {
	const { position, errorTitle, errors, duration = 2000, onClose } = Props;

	const [hidden, setHidden] = useState(true);
	const [pos, setPos] = useState<any>(calculatePos(position));

	useEffect(() => {
		if (errors && errors.length) {
			setHidden(false);
			const errorTimeout = setTimeout(() => {
				setHidden(true);
				onClose && onClose();
			}, duration);
			return () => clearTimeout(errorTimeout);
		}
	}, [errors]);

	useEffect(() => {
		setPos(calculatePos(position));
	}, [position]);

	return (
		<div
			className="fixed rounded-md bg-red-50 p-4"
			style={{
				top: pos.top,
				right: pos.right,
				bottom: pos.bottom,
				left: pos.left,
				zIndex: 1000,
			}}
			hidden={hidden}
		>
			<div className="flex">
				<div className="flex-shrink-0">
					<XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
				</div>
				<div className="ml-3">
					<h3 className="text-sm font-medium text-red-800">{errorTitle}</h3>
					<div className="mt-2 text-sm text-red-700">
						<ul role="list" className="list-disc space-y-1 pl-5">
							{errors.map((error, index) => (
								<li key={index}>{error}</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}
